/**
 * useBackendHealth — pinga /api/health/live/ ao montar e retorna o estado.
 *
 * Não usa a instância `api` (axios) padrão para evitar interceptors de
 * auth — esse check tem que funcionar mesmo sem token. Timeout curto
 * (5s) pra não travar a tela de login.
 */
import { useEffect, useState } from 'react'

export type HealthStatus = 'checking' | 'ok' | 'down'

const HEALTH_URL = (() => {
  const base =
    import.meta.env.VITE_API_BASE_URL ?? 'https://api.optimus.grupomdi.com.br'
  return `${base.replace(/\/$/, '')}/api/health/live/`
})()

/** Um ping de saúde avulso (o mesmo do hook), para confirmar uma suspeita. */
export async function isBackendUp(timeoutMs = 5000): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const r = await fetch(HEALTH_URL, {
      signal: controller.signal,
      cache: 'no-store',
    })
    return r.ok
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

export function useBackendHealth(): HealthStatus {
  const [status, setStatus] = useState<HealthStatus>('checking')

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 5000)

    fetch(HEALTH_URL, { signal: controller.signal, cache: 'no-store' })
      .then(r => {
        if (cancelled) return
        setStatus(r.ok ? 'ok' : 'down')
      })
      .catch(() => {
        if (!cancelled) setStatus('down')
      })
      .finally(() => clearTimeout(timer))

    return () => {
      cancelled = true
      controller.abort()
      clearTimeout(timer)
    }
  }, [])

  return status
}
